import { findFirstNonEmptyString, formatInTimeZone, notEmpty } from "@product/scmp-sdk";
import first from "lodash/first";
import last from "lodash/last";
import upperFirst from "lodash/upperFirst";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import type {
  CorrectionComment,
  ImageObject,
  NewsArticle,
  NewsMediaOrganization,
  Person,
  Place,
  Product,
  WebPageElement,
} from "schema-dts";

import { ValidArticleFlag } from "~/components/article/article-seo/consts";
import { getAbsoluteUrl, getAssetPrefix } from "~/lib/utils";
import type { articleSchemaContent$key } from "~/queries/__generated__/articleSchemaContent.graphql";

import { AdvertiserContentArticleSchema } from "./advertiser-content-article";
import { AnalysisNewsArticleSchema } from "./analysis-news-article";
import { BackgroundNewsArticleSchema } from "./background-news-article";
import { ArticleTypeToSchemaType, ISO8601DateTimeFormat, SchemaType } from "./consts";
import { ArticleSchemaContext } from "./contexts";
import { LiveBlogPostingSchema } from "./live-blog-posting";
import { OpinionNewsArticleSchema } from "./opinion-news-article";
import { ReportageNewsArticleSchema } from "./reportage-news-article";
import { ReviewNewsArticleSchema } from "./review-news-article";
import type { CreativeWorkUnionType, SwGCompatibleSchema } from "./types";

type Props = {
  reference: articleSchemaContent$key;
};

export const ArticleSchema: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const content = useFragment(
    graphql`
      fragment articleSchemaContent on Query
      @argumentDefinitions(entityId: { type: "String!" }, entityUuid: { type: "String!" }) {
        article(filter: { entityId: $entityId }) {
          authorLocations
          contentLock
          createdDate
          flag
          headline
          publishedDate
          socialHeadline
          sponsorType
          updatedDate
          urlAlias
          authors {
            location
            name
            types
            urlAlias
          }
          body(customContents: $customContents) {
            json
            text
          }
          corrections {
            correction {
              text
            }
            timestamp
          }
          images {
            isSlideshow
            height
            width
            url
          }
          sections {
            value {
              name
            }
          }
          types {
            value {
              name
            }
          }
        }
        ...liveBlogPostingLiveArticles @arguments(entityId: $entityId, entityUuid: $entityUuid)
      }
    `,
    reference_,
  );

  const mainAuthor = useMemo(() => first(content.article.authors ?? []), [content.article.authors]);

  const mainType = useMemo(() => {
    if (content.article.sponsorType) return SchemaType.AdvertiserContentArticle;
    const articleTypes = (content.article.types ?? [])
      .flatMap(type => (type?.value ?? []).map(value => value?.name ?? ""))
      .filter(type => notEmpty(type));
    const mainType = first(articleTypes) ?? "";
    return ArticleTypeToSchemaType[mainType] ?? SchemaType.ReportageNewsArticle;
  }, [content.article.sponsorType, content.article.types]);

  const noBylinesPolicy = useMemo(
    () =>
      (mainAuthor?.types ?? []).includes("no_byline")
        ? "https://www.scmp.com/policies-and-standards#no-byline-policy-explanation"
        : undefined,
    [mainAuthor?.types],
  );

  const publisher = useMemo<NewsMediaOrganization>(
    () => ({
      "@type": "NewsMediaOrganization",
      logo: {
        "@type": "ImageObject",
        height: {
          "@type": "QuantitativeValue",
          value: 512,
        },
        url: `${getAssetPrefix()}/icons/scmp-icon-512x512.png`,
        width: {
          "@type": "QuantitativeValue",
          value: 512,
        },
      },
      name: "South China Morning Post",
      noBylinesPolicy,
    }),
    [noBylinesPolicy],
  );

  const isPartOf = useMemo<SwGCompatibleSchema<NewsArticle, Product>>(
    () => ({
      "@type": ["NewsArticle", "Product"],
      name: "South China Morning Post",
      productID: "www.scmp.com:digital",
    }),
    [],
  );

  const author = useMemo<Person>(
    () => ({
      "@type": "Person",
      name: mainAuthor?.name ?? "SCMP Staff",
      sameAs: `${getAbsoluteUrl(mainAuthor?.urlAlias ?? "")}`,
    }),
    [mainAuthor?.name, mainAuthor?.urlAlias],
  );

  const locationCreated = useMemo<Place>(() => {
    const authorsLocation = first(content.article.authors ?? [])?.location ?? "";
    const articleLocation = first(content.article.authorLocations ?? []) ?? "";
    return {
      "@type": "Place",
      name: findFirstNonEmptyString(
        articleLocation.toLowerCase() === "not available" ? "" : articleLocation,
        authorsLocation,
        "Hong Kong",
      ),
    };
  }, [content.article.authorLocations, content.article.authors]);

  const description = useMemo(() => {
    for (const node of content.article.body?.json ?? []) {
      if (node.type !== "p" || !notEmpty(node.children)) continue;
      for (const child of node.children) {
        if (child.type !== "text" || !notEmpty(child.data)) continue;
        return child.data;
      }
    }
    return "";
  }, [content.article.body?.json]);

  const correction = useMemo<CorrectionComment | undefined>(() => {
    const firstCorrection = first(content.article.corrections ?? []);
    return firstCorrection
      ? {
          "@type": "CorrectionComment",
          datePublished: formatInTimeZone(new Date(firstCorrection.timestamp), "yyyy-MM-dd"),
          name: firstCorrection.correction?.text?.replaceAll(/<\/?[^>]+>/gi, ""),
        }
      : undefined;
  }, [content.article.corrections]);

  const mainSectionNames = useMemo(() => {
    if (!notEmpty(content.article.sections)) return ["News"];
    return first(content.article.sections)?.value?.map(section => section?.name);
  }, [content.article.sections]);

  const headline = useMemo(() => {
    if (!notEmpty(content.article.headline)) return "";
    const prefix = upperFirst(content.article.flag ?? "");
    const headline = content.article.headline ?? "";
    return ValidArticleFlag.includes(prefix.toUpperCase()) ? `${prefix}: ${headline}` : headline;
  }, [content.article.flag, content.article.headline]);

  const hasPart = useMemo<WebPageElement | undefined>(
    () =>
      content.article.contentLock
        ? {
            "@type": "WebPageElement",
            cssSelector: ".paywalled-content",
            isAccessibleForFree: "False",
          }
        : undefined,
    [content.article.contentLock],
  );

  const image = useMemo<ImageObject | undefined>(() => {
    if (!notEmpty(content.article.images)) return;
    const images = content.article.images?.filter(img => notEmpty(img)) ?? [];
    const mainImage = images.find(img => !!img?.isSlideshow) ?? first(images);
    return mainImage
      ? {
          "@type": "ImageObject",
          height: {
            "@type": "QuantitativeValue",
            value: mainImage?.height ?? 0,
          },
          url: mainImage?.url ?? "",
          width: {
            "@type": "QuantitativeValue",
            value: mainImage?.width ?? 0,
          },
        }
      : undefined;
  }, [content.article.images]);

  const schemaData = useMemo<CreativeWorkUnionType>(
    () => ({
      alternativeHeadline: content.article.socialHeadline ?? "",
      articleBody: content.article.body?.text ?? "",
      articleSection: last(mainSectionNames),
      author,
      correction,
      dateCreated: formatInTimeZone(content.article.createdDate ?? 0, ISO8601DateTimeFormat),
      dateModified: formatInTimeZone(content.article.updatedDate ?? 0, ISO8601DateTimeFormat),
      datePublished: formatInTimeZone(content.article.publishedDate ?? 0, ISO8601DateTimeFormat),
      description,
      genre: first(mainSectionNames),
      hasPart,
      headline,
      image,
      inLanguage: "en-GB",
      isAccessibleForFree: content.article.contentLock ? "False" : "True",
      isPartOf: isPartOf as any, // eslint-disable-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
      locationCreated,
      mainEntityOfPage: getAbsoluteUrl(content.article.urlAlias),
      publisher,
      publishingPrinciples: "https://www.scmp.com/policies-and-standards",
    }),
    [
      author,
      content.article.body?.text,
      content.article.contentLock,
      content.article.createdDate,
      content.article.publishedDate,
      content.article.socialHeadline,
      content.article.updatedDate,
      content.article.urlAlias,
      correction,
      description,
      hasPart,
      headline,
      image,
      isPartOf,
      locationCreated,
      publisher,
      mainSectionNames,
    ],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const componentMap: Record<string, JSX.Element | null> = {
    AdvertiserContentArticle: <AdvertiserContentArticleSchema />,
    AnalysisNewsArticle: <AnalysisNewsArticleSchema />,
    BackgroundNewsArticle: <BackgroundNewsArticleSchema />,
    LiveBlogPosting: content ? <LiveBlogPostingSchema reference={content} /> : null,
    NullNewsArticle: null,
    OpinionNewsArticle: <OpinionNewsArticleSchema />,
    ReportageNewsArticle: <ReportageNewsArticleSchema />,
    ReviewNewsArticle: <ReviewNewsArticleSchema />,
  };

  const renderJsonLd = () => (
    <ArticleSchemaContext.Provider value={{ schemaData }}>
      {componentMap[mainType]}
    </ArticleSchemaContext.Provider>
  );

  return renderJsonLd();
};

ArticleSchema.displayName = "ArticleSchema";
